/**
 * StructureTimeline — Professional interactive song structure editor
 * 
 * 6 critical features:
 * 1. Real-time synced playhead line
 * 2. Duration total + time grid per section
 * 3. Resize sections by dragging edges (snap to bars)
 * 4. Smart change animations (highlight what changed)
 * 5. Energy arc visualization (narrative progression)
 * 6. Instant feedback on every interaction (<100ms)
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Zap, Music2, Sparkles, Waves, Plus, X, GripVertical, Copy } from 'lucide-react';

// ── Section type definitions ──
export const SECTION_TYPES = {
  intro:      { name: 'INTRO',  color: '#3F3F46', icon: Waves,    description: 'Build-up inicial', defaultBars: 8 },
  verse:      { name: 'VERSE',  color: '#7C3AED', icon: Music2,   description: 'Melodia principal', defaultBars: 16 },
  prechorus:  { name: 'PRE',    color: '#F5C842', icon: Sparkles, description: 'Tension pre-drop',  defaultBars: 8 },
  drop:       { name: 'DROP',   color: '#FF6B00', icon: Zap,      description: 'Maxima energia',    defaultBars: 16 },
  breakdown:  { name: 'BREAK',  color: '#0891B2', icon: Waves,    description: 'Bajada de energia', defaultBars: 8 },
  outro:      { name: 'OUTRO',  color: '#52525B', icon: Waves,    description: 'Cierre del track',  defaultBars: 8 },
};

// ── Energy levels per section type (1-10) ──
const SECTION_ENERGY = {
  intro: 3, verse: 5, prechorus: 7, drop: 10, breakdown: 4, outro: 2,
};

function energyGlow(level) {
  if (level >= 9) return { color: '#FF4444', shadow: '0 0 12px #FF444440', intensity: 1 };
  if (level >= 7) return { color: '#FF6B00', shadow: '0 0 10px #FF6B0030', intensity: 0.85 };
  if (level >= 5) return { color: '#F5C842', shadow: '0 0 8px #F5C84225', intensity: 0.7 };
  if (level >= 3) return { color: '#52525B', shadow: 'none', intensity: 0.5 };
  return { color: '#3F3F46', shadow: 'none', intensity: 0.35 };
}

// ── Default structures by genre ──
export const GENRE_STRUCTURES = {
  trap:       ['intro', 'verse', 'prechorus', 'drop', 'verse', 'drop', 'outro'],
  reggaeton:  ['intro', 'verse', 'prechorus', 'drop', 'verse', 'drop', 'breakdown', 'drop', 'outro'],
  electronic: ['intro', 'breakdown', 'prechorus', 'drop', 'breakdown', 'drop', 'outro'],
  pop:        ['intro', 'verse', 'prechorus', 'drop', 'verse', 'prechorus', 'drop', 'outro'],
  rnb:        ['intro', 'verse', 'verse', 'prechorus', 'drop', 'verse', 'drop', 'outro'],
  default:    ['intro', 'verse', 'drop', 'verse', 'drop', 'outro'],
};

export function generateDefaultSections(genre, totalDuration, bpm = 120) {
  const barLength = (60 / bpm) * 4;
  const structure = GENRE_STRUCTURES[genre] || GENRE_STRUCTURES.default;
  const totalBars = structure.reduce((sum, type) => sum + (SECTION_TYPES[type]?.defaultBars || 8), 0);
  const scaleFactor = totalDuration / (totalBars * barLength);
  const sections = [];
  let t = 0;
  structure.forEach((type) => {
    const bars = Math.max(2, Math.round((SECTION_TYPES[type]?.defaultBars || 8) * scaleFactor));
    const dur = bars * barLength;
    sections.push({ type, startTime: t, endTime: t + dur, bars });
    t += dur;
  });
  return sections;
}

export function recalcSections(sections, bpm = 120) {
  const barLength = (60 / bpm) * 4;
  let t = 0;
  return sections.map(s => {
    const dur = s.bars * barLength;
    const updated = { ...s, startTime: t, endTime: t + dur };
    t += dur;
    return updated;
  });
}

function fmtTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ── Energy Arc SVG ──
function EnergyArc({ sections, totalDuration, height = 16 }) {
  if (sections.length < 2) return null;
  const points = sections.map((s) => {
    const x = ((s.startTime + (s.endTime - s.startTime) / 2) / totalDuration) * 100;
    const energy = SECTION_ENERGY[s.type] || 5;
    const y = height - (energy / 10) * (height - 2);
    return { x, y, energy, type: s.type };
  });

  // Build smooth curve path
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.x + curr.x) / 2;
    path += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
  }

  return (
    <svg className="absolute inset-0 pointer-events-none z-10" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none"
      style={{ width: '100%', height: `${height}px` }}>
      <defs>
        <linearGradient id="energyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          {points.map((p, i) => (
            <stop key={i} offset={`${p.x}%`} stopColor={energyGlow(p.energy).color} stopOpacity="0.6" />
          ))}
        </linearGradient>
      </defs>
      <path d={path} fill="none" stroke="url(#energyGrad)" strokeWidth="0.8" strokeLinecap="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="1.2"
          fill={energyGlow(p.energy).color} opacity="0.9" />
      ))}
    </svg>
  );
}

// ── Main Component ──
export function StructureTimeline({
  sections = [],
  currentTime = 0,
  duration = 180,
  bpm = 120,
  genre = 'default',
  editable = false,
  compact = false,
  loopRegion = null,
  onSectionClick,
  onSectionsChange,
  onLoopRegionChange,
  className = '',
}) {
  const [hoveredSection, setHoveredSection] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [addMenuIndex, setAddMenuIndex] = useState(null);
  const [resizeIdx, setResizeIdx] = useState(null);
  const [changedSet, setChangedSet] = useState(new Set());
  // Loop region selection state
  const [loopSelecting, setLoopSelecting] = useState(false);
  const [loopDragStart, setLoopDragStart] = useState(null);
  const [loopDragEnd, setLoopDragEnd] = useState(null);
  const [loopHandleDrag, setLoopHandleDrag] = useState(null); // 'start' | 'end' | null
  const timelineRef = useRef(null);
  const playheadRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartBars = useRef(0);
  const containerW = useRef(500);
  const prevSectionsRef = useRef([]);

  const activeSections = sections.length > 0
    ? sections
    : generateDefaultSections(genre, duration, bpm);

  const totalDuration = activeSections.length > 0
    ? activeSections[activeSections.length - 1].endTime
    : duration;
  const totalBars = activeSections.reduce((s, sec) => s + sec.bars, 0);

  const currentSectionIndex = activeSections.findIndex((s, i) => {
    const next = activeSections[i + 1];
    return currentTime >= s.startTime && (next ? currentTime < next.startTime : true);
  });

  // ── Playhead (ref-based for performance) ──
  useEffect(() => {
    if (!playheadRef.current || totalDuration <= 0) return;
    const pct = Math.min(100, (currentTime / totalDuration) * 100);
    playheadRef.current.style.left = `${pct}%`;
    playheadRef.current.style.opacity = currentTime > 0 ? '1' : '0';
  }, [currentTime, totalDuration]);

  // ── Change detection & animation ──
  useEffect(() => {
    const prev = prevSectionsRef.current;
    const curr = activeSections;
    if (prev.length === 0 || JSON.stringify(prev) === JSON.stringify(curr)) {
      prevSectionsRef.current = [...curr];
      return;
    }
    const changed = new Set();
    curr.forEach((s, i) => {
      const p = prev[i];
      if (!p || p.type !== s.type || p.bars !== s.bars) changed.add(i);
    });
    for (let i = prev.length; i < curr.length; i++) changed.add(i);
    if (changed.size > 0) {
      setChangedSet(changed);
      const timer = setTimeout(() => setChangedSet(new Set()), 1200);
      prevSectionsRef.current = [...curr];
      return () => clearTimeout(timer);
    }
    prevSectionsRef.current = [...curr];
  }, [activeSections]);

  // ── Resize handlers ──
  const startResize = (e, idx) => {
    e.stopPropagation();
    e.preventDefault();
    setResizeIdx(idx);
    resizeStartX.current = e.clientX;
    resizeStartBars.current = activeSections[idx].bars;
    containerW.current = timelineRef.current?.getBoundingClientRect().width || 500;
  };

  useEffect(() => {
    if (resizeIdx === null) return;
    const handleMove = (e) => {
      const dx = e.clientX - resizeStartX.current;
      const pxPerBar = containerW.current / totalBars;
      const deltaBars = Math.round(dx / pxPerBar);
      const newBars = Math.max(2, resizeStartBars.current + deltaBars);
      const curr = activeSections[resizeIdx];
      if (curr && newBars !== curr.bars) {
        const updated = activeSections.map((s, i) =>
          i === resizeIdx ? { ...s, bars: newBars } : s
        );
        onSectionsChange?.(recalcSections(updated, bpm));
      }
    };
    const handleUp = () => setResizeIdx(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [resizeIdx, activeSections, totalBars, bpm, onSectionsChange]);

  // ── Loop region selection (non-edit mode: drag to create loop) ──
  const pctFromMouseX = useCallback((clientX) => {
    if (!timelineRef.current) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const snapToBar = useCallback((pct) => {
    // Snap percentage to nearest bar boundary
    const totalBarsCalc = activeSections.reduce((s, sec) => s + sec.bars, 0);
    if (totalBarsCalc === 0) return pct;
    const barPct = 1 / totalBarsCalc;
    return Math.round(pct / barPct) * barPct;
  }, [activeSections]);

  const handleTimelineMouseDown = useCallback((e) => {
    if (editable || resizeIdx !== null) return; // Don't interfere with edit mode
    // Check if clicking a loop handle
    if (loopRegion && e.target.closest('[data-loop-handle]')) return;
    const pct = pctFromMouseX(e.clientX);
    setLoopSelecting(true);
    setLoopDragStart(pct);
    setLoopDragEnd(pct);
  }, [editable, resizeIdx, pctFromMouseX, loopRegion]);

  useEffect(() => {
    if (!loopSelecting) return;
    const handleMove = (e) => {
      setLoopDragEnd(pctFromMouseX(e.clientX));
    };
    const handleUp = () => {
      setLoopSelecting(false);
      if (loopDragStart != null && loopDragEnd != null) {
        const s = snapToBar(Math.min(loopDragStart, loopDragEnd));
        const en = snapToBar(Math.max(loopDragStart, loopDragEnd));
        if (en - s > 0.02) { // Minimum 2% width to count as a region
          onLoopRegionChange?.({ startPct: s, endPct: en });
        } else {
          // Too small = click, not drag. Seek instead.
          onSectionClick?.(activeSections.find((sec, i) => {
            const secStart = sec.startTime / totalDuration;
            const secEnd = sec.endTime / totalDuration;
            return s >= secStart && s < secEnd;
          }));
        }
      }
      setLoopDragStart(null);
      setLoopDragEnd(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [loopSelecting, loopDragStart, loopDragEnd, snapToBar, pctFromMouseX, onLoopRegionChange, onSectionClick, activeSections, totalDuration]);

  // Loop handle drag
  useEffect(() => {
    if (!loopHandleDrag || !loopRegion) return;
    const handleMove = (e) => {
      const pct = snapToBar(pctFromMouseX(e.clientX));
      if (loopHandleDrag === 'start') {
        if (pct < loopRegion.endPct - 0.02) {
          onLoopRegionChange?.({ ...loopRegion, startPct: pct });
        }
      } else {
        if (pct > loopRegion.startPct + 0.02) {
          onLoopRegionChange?.({ ...loopRegion, endPct: pct });
        }
      }
    };
    const handleUp = () => setLoopHandleDrag(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [loopHandleDrag, loopRegion, snapToBar, pctFromMouseX, onLoopRegionChange]);

  // ── Drag handlers ──
  const handleDragStart = (e, idx) => {
    if (!editable) return;
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx.toString());
  };

  const handleDragOver = useCallback((e, idx) => {
    if (!editable || dragIndex === null) return;
    e.preventDefault();
    if (idx !== dragIndex) setDragOverIndex(idx);
  }, [editable, dragIndex]);

  const handleDrop = useCallback((e, toIdx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === toIdx || !editable) return;
    const arr = [...activeSections];
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(toIdx, 0, moved);
    onSectionsChange?.(recalcSections(arr, bpm));
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, activeSections, bpm, editable, onSectionsChange]);

  const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

  // ── Section operations ──
  const addSection = (type, afterIdx) => {
    const arr = [...activeSections];
    arr.splice(afterIdx + 1, 0, { type, bars: SECTION_TYPES[type]?.defaultBars || 8, startTime: 0, endTime: 0 });
    onSectionsChange?.(recalcSections(arr, bpm));
    setAddMenuIndex(null);
  };

  const removeSection = (idx) => {
    if (activeSections.length <= 2) return;
    onSectionsChange?.(recalcSections(activeSections.filter((_, i) => i !== idx), bpm));
  };

  const duplicateSection = (idx) => {
    const arr = [...activeSections];
    arr.splice(idx + 1, 0, { ...arr[idx] });
    onSectionsChange?.(recalcSections(arr, bpm));
  };

  // ── Compact mode ──
  if (compact) {
    return (
      <div className={`w-full ${className}`} data-testid="structure-timeline">
        <div className="flex h-2 rounded-full overflow-hidden gap-px">
          {activeSections.map((s, i) => {
            const st = SECTION_TYPES[s.type] || SECTION_TYPES.verse;
            const w = ((s.endTime - s.startTime) / totalDuration) * 100;
            return (
              <div key={i} className={`h-full transition-all cursor-pointer ${i === currentSectionIndex ? 'ring-1 ring-white' : ''}`}
                style={{ width: `${w}%`, backgroundColor: st.color, opacity: i === currentSectionIndex ? 1 : 0.6 }}
                onClick={() => onSectionClick?.(s)}
                title={`${st.name} ${fmtTime(s.startTime)}`}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // ── Full interactive mode ──
  return (
    <div className={`w-full select-none ${className}`} data-testid="structure-timeline" ref={timelineRef}>
      {/* Energy arc + Playhead container */}
      <div className="relative">
        {/* Energy arc visualization */}
        {activeSections.length >= 2 && (
          <EnergyArc sections={activeSections} totalDuration={totalDuration} height={14} />
        )}

        {/* Section blocks */}
        <div
          className={`flex gap-0 items-stretch relative ${!editable ? 'cursor-crosshair' : ''}`}
          style={{ paddingTop: '14px' }}
          onMouseDown={handleTimelineMouseDown}
          onDoubleClick={() => { if (!editable) onLoopRegionChange?.(null); }}
        >
          {/* Playhead line */}
          <div
            ref={playheadRef}
            className="absolute top-0 bottom-0 w-[2px] z-20 pointer-events-none transition-opacity"
            style={{
              background: 'linear-gradient(to bottom, #F5C842, #F5C842CC, #F5C84266)',
              left: '0%',
              opacity: 0,
              boxShadow: '0 0 6px #F5C84260',
            }}
            data-testid="structure-playhead"
          >
            <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#F5C842] shadow-[0_0_6px_#F5C842]" />
          </div>

          {/* Loop region selection preview (while dragging) */}
          {loopSelecting && loopDragStart != null && loopDragEnd != null && (
            <div className="absolute top-0 bottom-0 bg-[#F5C84220] border-l border-r border-[#F5C842]/40 z-15 pointer-events-none"
              style={{
                left: `${Math.min(loopDragStart, loopDragEnd) * 100}%`,
                width: `${Math.abs(loopDragEnd - loopDragStart) * 100}%`,
              }}
              data-testid="loop-selection-preview" />
          )}

          {/* Active loop region overlay + handles */}
          {loopRegion && !loopSelecting && (
            <>
              {/* Dim outside loop (left) */}
              <div className="absolute top-0 bottom-0 bg-black/40 pointer-events-none z-12 rounded-l-sm loop-region-fade"
                style={{ left: 0, width: `${loopRegion.startPct * 100}%` }} />
              {/* Loop highlight */}
              <div className="absolute top-0 bottom-0 z-12 pointer-events-none loop-region-fade"
                style={{
                  left: `${loopRegion.startPct * 100}%`,
                  width: `${(loopRegion.endPct - loopRegion.startPct) * 100}%`,
                  background: 'linear-gradient(to bottom, #F5C84210, #F5C84218, #F5C84210)',
                  borderTop: '2px solid #F5C84260',
                  borderBottom: '2px solid #F5C84260',
                }}
                data-testid="loop-region-highlight" />
              {/* Dim outside loop (right) */}
              <div className="absolute top-0 bottom-0 bg-black/40 pointer-events-none z-12 rounded-r-sm loop-region-fade"
                style={{ left: `${loopRegion.endPct * 100}%`, right: 0 }} />
              {/* Start handle */}
              <div
                data-loop-handle="start"
                className="absolute top-0 bottom-0 w-2 cursor-col-resize z-25 flex items-center justify-center group"
                style={{ left: `calc(${loopRegion.startPct * 100}% - 4px)` }}
                onMouseDown={(e) => { e.stopPropagation(); setLoopHandleDrag('start'); }}
                data-testid="loop-handle-start"
              >
                <div className="w-[3px] h-6 rounded-full bg-[#F5C842] group-hover:bg-[#FFD700] transition-all shadow-[0_0_6px_#F5C84260] group-hover:shadow-[0_0_10px_#F5C842]" />
              </div>
              {/* End handle */}
              <div
                data-loop-handle="end"
                className="absolute top-0 bottom-0 w-2 cursor-col-resize z-25 flex items-center justify-center group"
                style={{ left: `calc(${loopRegion.endPct * 100}% - 4px)` }}
                onMouseDown={(e) => { e.stopPropagation(); setLoopHandleDrag('end'); }}
                data-testid="loop-handle-end"
              >
                <div className="w-[3px] h-6 rounded-full bg-[#F5C842] group-hover:bg-[#FFD700] transition-all shadow-[0_0_6px_#F5C84260] group-hover:shadow-[0_0_10px_#F5C842]" />
              </div>
            </>
          )}

          {activeSections.map((section, idx) => {
            const st = SECTION_TYPES[section.type] || SECTION_TYPES.verse;
            const Icon = st.icon;
            const energy = SECTION_ENERGY[section.type] || 5;
            const glow = energyGlow(energy);
            const w = totalDuration > 0 ? ((section.endTime - section.startTime) / totalDuration) * 100 : 10;
            const isActive = idx === currentSectionIndex;
            const isHovered = hoveredSection === idx;
            const isDragging = dragIndex === idx;
            const isDragOver = dragOverIndex === idx;
            const isChanged = changedSet.has(idx);
            const isResizing = resizeIdx === idx;

            return (
              <React.Fragment key={`s-${idx}`}>
                {/* Drop zone */}
                {editable && isDragOver && dragIndex !== idx && (
                  <div className="w-1 bg-[#F5C842] rounded-full self-stretch flex-shrink-0 animate-pulse" />
                )}

                <div
                  className={`
                    relative flex flex-col justify-between rounded-sm overflow-hidden transition-all
                    ${editable && !isResizing ? 'cursor-grab active:cursor-grabbing' : !editable ? 'cursor-pointer' : ''}
                    ${isDragging ? 'opacity-30 scale-95' : ''}
                    ${isChanged ? 'structure-flash' : ''}
                  `}
                  style={{
                    width: `${w}%`,
                    minWidth: editable ? '36px' : '24px',
                    height: '52px',
                    background: `linear-gradient(to bottom, ${st.color}${isActive ? 'DD' : isDragOver ? 'AA' : '44'}, ${st.color}${isActive ? '99' : '22'})`,
                    borderLeft: idx > 0 ? '1px solid #0A0A0B' : 'none',
                    boxShadow: isActive
                      ? `inset 0 0 20px ${st.color}40, ${glow.shadow}`
                      : isChanged
                        ? `inset 0 0 16px ${st.color}60, 0 0 12px ${st.color}40`
                        : 'none',
                  }}
                  draggable={editable && !isResizing}
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  onClick={() => !editable && onSectionClick?.(section, idx)}
                  onMouseEnter={() => setHoveredSection(idx)}
                  onMouseLeave={() => { setHoveredSection(null); }}
                  data-testid={`section-${section.type}-${idx}`}
                >
                  {/* Top row: icon + name */}
                  <div className="flex items-center gap-0.5 px-1 pt-1">
                    {editable && isHovered && !isDragging && (
                      <GripVertical size={7} className="text-white/30 flex-shrink-0" />
                    )}
                    <Icon size={9} className="opacity-70 flex-shrink-0" style={{ color: isActive ? '#fff' : st.color }} />
                    {(w > 8 || isHovered) && (
                      <span className="text-[7px] sm:text-[8px] font-bold tracking-wider opacity-80 truncate"
                        style={{ color: isActive ? '#fff' : `${st.color}CC` }}>
                        {st.name}
                      </span>
                    )}
                  </div>

                  {/* Middle: bars count */}
                  <div className="flex items-center justify-center">
                    <span className={`text-[8px] font-mono tabular-nums transition-all ${isActive ? 'text-white/90' : 'text-white/40'}`}>
                      {section.bars}
                      <span className="text-[6px] opacity-60">b</span>
                    </span>
                  </div>

                  {/* Bottom: time marker */}
                  <div className="px-1 pb-0.5">
                    <span className="text-[6px] sm:text-[7px] font-mono text-white/30 tabular-nums">
                      {fmtTime(section.startTime)}
                    </span>
                  </div>

                  {/* Energy bar at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 h-[2px]"
                    style={{
                      background: `linear-gradient(to right, ${glow.color}00, ${glow.color}${Math.round(glow.intensity * 255).toString(16).padStart(2, '0')}, ${glow.color}00)`,
                    }} />

                  {/* Resize handle (right edge) */}
                  {editable && (
                    <div
                      className="absolute right-0 top-0 bottom-0 w-[5px] cursor-col-resize z-10 group/resize"
                      onMouseDown={(e) => startResize(e, idx)}
                      data-testid={`resize-handle-${idx}`}
                    >
                      <div className={`absolute right-0 top-1 bottom-1 w-[2px] rounded-full transition-all ${
                        isHovered || isResizing ? 'bg-[#F5C842]/60' : 'bg-transparent'
                      }`} />
                    </div>
                  )}

                  {/* Remove + Duplicate buttons (hover, editable) */}
                  {editable && isHovered && !isDragging && activeSections.length > 2 && (
                    <div className="absolute -top-1 right-0 flex gap-0.5 z-20">
                      <button
                        onClick={(e) => { e.stopPropagation(); duplicateSection(idx); }}
                        className="w-3.5 h-3.5 rounded-full bg-[#27272A] text-[#A1A1AA] flex items-center justify-center hover:bg-[#F5C842] hover:text-black transition-colors"
                        title="Duplicar"
                        data-testid={`duplicate-section-${idx}`}
                      >
                        <Copy size={7} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeSection(idx); }}
                        className="w-3.5 h-3.5 rounded-full bg-red-500/80 text-white flex items-center justify-center hover:bg-red-400 transition-colors"
                        data-testid={`remove-section-${idx}`}
                      >
                        <X size={7} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Add section button (between blocks) */}
                {editable && idx < activeSections.length - 1 && (
                  <div className="relative flex-shrink-0 self-stretch flex items-center" style={{ width: '10px' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setAddMenuIndex(addMenuIndex === idx ? null : idx); }}
                      className="w-2.5 h-2.5 rounded-full bg-[#1E1E23] hover:bg-[#F5C842] text-[#3F3F46] hover:text-black flex items-center justify-center transition-all mx-auto hover:scale-125"
                      data-testid={`add-section-after-${idx}`}
                    >
                      <Plus size={6} />
                    </button>
                    {addMenuIndex === idx && (
                      <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-[#1E1E23] border border-[#3F3F46] rounded-lg p-1 z-40 shadow-xl min-w-[76px]"
                        onMouseLeave={() => setAddMenuIndex(null)}>
                        {Object.entries(SECTION_TYPES).map(([key, val]) => (
                          <button key={key}
                            onClick={(e) => { e.stopPropagation(); addSection(key, idx); }}
                            className="w-full flex items-center gap-1.5 px-1.5 py-0.5 rounded text-left hover:bg-[#27272A] transition-colors"
                            data-testid={`add-${key}-after-${idx}`}
                          >
                            <div className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: val.color }} />
                            <span className="text-[7px] font-bold text-white">{val.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Info bar: legend + loop hint + total duration */}
      <div className="flex items-center justify-between mt-1 px-0.5">
        <div className="flex items-center gap-1.5">
          {Object.entries(SECTION_TYPES).slice(0, 5).map(([key, val]) => (
            <div key={key} className="flex items-center gap-0.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: val.color }} />
              <span className="text-[6px] text-[#3F3F46]">{val.name}</span>
            </div>
          ))}
          {!editable && !loopRegion && (
            <span className="text-[6px] text-[#27272A] ml-1">arrastra para crear loop</span>
          )}
          {loopRegion && (
            <span className="text-[6px] text-[#F5C842]/50 ml-1">doble-click para quitar loop</span>
          )}
        </div>
        <div className="flex items-center gap-2" data-testid="structure-total-info">
          <span className="text-[7px] font-mono text-[#52525B] tabular-nums">{totalBars} compases</span>
          <span className="text-[7px] font-mono text-[#71717A] font-bold tabular-nums">{fmtTime(totalDuration)}</span>
        </div>
      </div>
    </div>
  );
}

export default StructureTimeline;
